/**
 * @file Renders the shared antd drawer shell that other drawers wrap.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { Drawer as AntDrawer } from "antd";
import type { DrawerProps } from "antd";

import { HEADER_HEIGHT, DRAWER_WIDTH } from "@components/Constants";

import type { DrawerRef } from "./types";

type Props = {
  styles?: DrawerProps["styles"];
  extra?: ReactNode;
  onOpen?: (target?: unknown) => void;
  onClose?: () => void;
  children?: ReactNode;
  title: ReactNode;
  drawer: DrawerRef;
} & Omit<
  DrawerProps,
  "open" | "title" | "extra" | "styles" | "onClose" | "children"
>;

/**
 * Renders the antd drawer and exposes its open and close controls
 * through the `drawer` ref.
 */
const Drawer = ({
  styles: customStyles = {},
  extra = undefined,
  onOpen = () => undefined,
  onClose = () => undefined,
  children = undefined,
  title,
  drawer,
  ...otherDrawerProps
}: Props): JSX.Element => {
  const [ isOpen, setIsOpen ] = useState(false);

  /**
   * Runs the open callback and shows the drawer.
   */
  const onDrawerOpen = (target?: unknown) => {
    onOpen(target);
    setIsOpen(true);
  };

  /**
   * Hides the drawer and runs the close callback.
   */
  const onDrawerClose = () => {
    setIsOpen(false);
    onClose();
  };

  /**
   * Opens the drawer through the `drawer` ref.
   */
  drawer.current.open = (target?: unknown) =>
    onDrawerOpen(target);

  /**
   * Closes the drawer through the `drawer` ref.
   */
  drawer.current.close = () =>
    onDrawerClose();

  const styles = {
    header: { minHeight: HEADER_HEIGHT + 1 },
    ...customStyles,
  };

  return (
    <AntDrawer
      open={isOpen}
      title={title}
      extra={extra}
      width={DRAWER_WIDTH}
      styles={styles}
      onClose={onDrawerClose}
      {...otherDrawerProps}
    >
      {
        children && (
          <>
            {children}
          </>
        )
      }
    </AntDrawer>
  );
};

export default Drawer;
